import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Timeline — horizontal histogram of transactions over time.
 * Brush-selects a time range; calls onRangeChange([startMs, endMs] | null).
 * Play button auto-advances the brush window forward.
 */

const HANDLE_W  = 8;
const PLAY_STEP = 0.005;
const PLAY_INTERVAL_MS = 60;

const AXIS_H = 20;
const BAR_PAD_TOP = 4;
const BAR_PAD_BOT = 3;

function buildBins(txBuckets, binCount) {
    const allTimes = [];
    for (const bucket of txBuckets) {
        for (const t of (bucket.transactions || [])) {
            if (t.time instanceof Date && !isNaN(t.time)) {
                allTimes.push(t.time.getTime());
            }
        }
    }
    if (!allTimes.length) {
        return { bins: [], minT: 0, maxT: 0 };
    }

    allTimes.sort((a, b) => a - b);
    const minT = allTimes[0];
    const maxT = allTimes[allTimes.length - 1];
    const range = maxT - minT || 1;

    const bins = new Array(binCount).fill(0);
    for (const t of allTimes) {
        const idx = Math.min(Math.floor(((t - minT) / range) * binCount), binCount - 1);
        bins[idx]++;
    }

    return { bins, minT, maxT };
}

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function formatAxisLabel(ms, showMonth) {
    const d = new Date(ms);
    if (showMonth) {
        return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
    }
    return `${d.getFullYear()}`;
}

/**
 * Multi-scale tick generator.
 *
 * Returns { majorTicks, minorTicks }
 *   majorTick  = { ts, label }   – rendered with a label
 *   minorTick  = { ts }          – short tick mark only (no label)
 *
 * Scale thresholds (by total visible days):
 *   ≤ 60 days  → "small"   : major = days (with day number labels),
 *                             step chosen so labels don't overlap
 *   ≤ 180 days → "medium"  : major = months,  minor = every day (no label)
 *   > 180 days → "large"   : major = months/years, no minor day ticks
 */
function generateTicks(minT, maxT, maxTicks) {
    const dMin = new Date(minT);
    const dMax = new Date(maxT);
    const totalDays = (maxT - minT) / 86_400_000;
    const totalMonths =
        (dMax.getFullYear() - dMin.getFullYear()) * 12 +
        (dMax.getMonth() - dMin.getMonth()) + 1;

    const majorTicks = [];
    const minorTicks = [];

    // ── Small scale: ≤ 60 days — day ticks with labels ──────
    if (totalDays <= 60) {
        // Pick a day step so labels don't collide (assume ~30px per label)
        const dayStep = Math.max(1, Math.ceil(totalDays / maxTicks));

        // Start from midnight of first full day after minT
        const start = new Date(dMin.getFullYear(), dMin.getMonth(), dMin.getDate());
        if (start.getTime() < minT) {
            start.setDate(start.getDate() + 1);
        }

        // Also generate month-boundary majors for context
        const monthBoundaries = new Set();
        {
            let my = dMin.getFullYear();
            let mm = dMin.getMonth() + 1;
            if (mm > 11) {
                mm = 0;
                my++;
            }
            for (let i = 0; i < 10; i++) {
                const mts = new Date(my, mm, 1).getTime();
                if (mts > maxT) {
                    break;
                }
                if (mts >= minT) {
                    monthBoundaries.add(mts);
                }
                mm++;
                if (mm > 11) {
                    mm = 0;
                    my++;
                }
            }
        }

        const cur = new Date(start);
        let labelIdx = 0;
        for (let i = 0; i < 200; i++) {
            const ts = cur.getTime();
            if (ts > maxT) {
                break;
            }
            if (ts >= minT) {
                const isFirst = cur.getDate() === 1;
                if (isFirst && monthBoundaries.has(ts)) {
                    // Month boundary gets a "Mon D" label
                    majorTicks.push({
                        ts,
                        label: `${MONTH_NAMES[cur.getMonth()]} ${cur.getDate()}`,
                    });
                    monthBoundaries.delete(ts);
                    labelIdx = 0;
                } else if (labelIdx % dayStep === 0) {
                    majorTicks.push({ ts, label: String(cur.getDate()) });
                } else {
                    minorTicks.push({ ts });
                }
                labelIdx++;
            }
            cur.setDate(cur.getDate() + 1);
        }

        // Add remaining month boundaries that didn't coincide with a day
        for (const mts of monthBoundaries) {
            const md = new Date(mts);
            majorTicks.push({
                ts: mts,
                label: `${MONTH_NAMES[md.getMonth()]} ${md.getDate()}`,
            });
        }

        majorTicks.sort((a, b) => a.ts - b.ts);
        return { majorTicks, minorTicks };
    }

    // ── Medium scale: 61–180 days — months labelled, days as minor ──
    if (totalDays <= 180) {
        // Minor ticks: every day (no label)
        const dayStart = new Date(dMin.getFullYear(), dMin.getMonth(), dMin.getDate());
        if (dayStart.getTime() < minT) {
            dayStart.setDate(dayStart.getDate() + 1);
        }
        const dc = new Date(dayStart);
        for (let i = 0; i < 400; i++) {
            const ts = dc.getTime();
            if (ts > maxT) {
                break;
            }
            if (ts >= minT && dc.getDate() !== 1) {
                minorTicks.push({ ts });
            }
            dc.setDate(dc.getDate() + 1);
        }

        // Major ticks: month boundaries
        let y = dMin.getFullYear();
        let m = dMin.getMonth();
        if (dMin.getDate() > 1) {
            m++;
            if (m > 11) {
                m = 0;
                y++;
            }
        }
        for (let i = 0; i < 50; i++) {
            const ts = new Date(y, m, 1).getTime();
            if (ts > maxT) {
                break;
            }
            if (ts >= minT) {
                const md = new Date(ts);
                majorTicks.push({
                    ts,
                    label: `${MONTH_NAMES[md.getMonth()]} ${md.getFullYear()}`,
                });
            }
            m++;
            if (m > 11) {
                m = 0;
                y++;
            }
        }

        return { majorTicks, minorTicks };
    }

    // ── Large scale: > 180 days — months / years only ───────
    const step = Math.max(1, Math.ceil(totalMonths / maxTicks));

    let y = dMin.getFullYear();
    let m = dMin.getMonth();
    if (dMin.getDate() > 1 || dMin.getHours() > 0) {
        m += 1;
        if (m > 11) {
            m = 0;
            y++;
        }
    }

    const showMonth = totalMonths <= maxTicks * 3;
    for (let i = 0; i < 200; i++) {
        const ts = new Date(y, m, 1).getTime();
        if (ts > maxT) {
            break;
        }
        if (ts >= minT) {
            majorTicks.push({ ts, label: formatAxisLabel(ts, showMonth) });
        }
        m += step;
        while (m > 11) {
            m -= 12;
            y++;
        }
    }

    return { majorTicks, minorTicks };
}

export default function Timeline({ txBuckets, onRangeChange }) {
    const canvasRef  = useRef(null);
    const wrapRef    = useRef(null);
    const [dims, setDims]       = useState({ w: 600, h: 100 });
    const [brush, setBrush]     = useState(null);
    const [playing, setPlaying] = useState(false);
    const dragging  = useRef(null);
    const dragStart = useRef({ x: 0, left: 0, right: 0 });
    const playRef   = useRef(null);
    const brushRef  = useRef(brush);

    useEffect(() => {
        brushRef.current = brush;
    }, [brush]);

    const BIN_COUNT = Math.max(30, Math.floor(dims.w / 6));

    const { bins, minT, maxT } = useMemo(() => {
        return buildBins(txBuckets, BIN_COUNT);
    }, [txBuckets, BIN_COUNT]);

    const maxBin = Math.max(1, ...bins);
    const range  = maxT - minT || 1;
    const hasBins = bins.length > 0;

    const ticksData = useMemo(() => {
        if (!minT || !maxT || minT === maxT) {
            return { majorTicks: [], minorTicks: [] };
        }
        const maxLabels = Math.max(3, Math.floor(dims.w / 120));
        return generateTicks(minT, maxT, maxLabels);
    }, [minT, maxT, dims.w]);

    // ── Resize observer ─────────────────────────────────────
    // Observe the wrapper div. contentRect gives us the inner
    // content-box size (already excludes padding-left for the
    // play button). We floor to whole pixels so the canvas
    // backing-store and CSS size are identical → no stretching.
    useEffect(() => {
        const wrap = wrapRef.current;
        if (!wrap) {
            return;
        }
        const ro = new ResizeObserver(entries => {
            for (const e of entries) {
                const w = e.contentRect.width;
                const h = e.contentRect.height;
                if (w > 0 && h > 0) {
                    setDims({ w: Math.floor(w), h: Math.floor(h) });
                }
            }
        });
        ro.observe(wrap);
        return () => ro.disconnect();
    }, [hasBins]);

    // ── Play / stop ─────────────────────────────────────────
    const stopPlay = useCallback(() => {
        setPlaying(false);
        if (playRef.current) {
            clearInterval(playRef.current);
            playRef.current = null;
        }
    }, []);

    const startPlay = useCallback(() => {
        if (!brushRef.current) {
            setBrush({ left: 0, right: 0.08 });
        }
        setPlaying(true);
    }, []);

    useEffect(() => {
        if (!playing) {
            return;
        }
        playRef.current = setInterval(() => {
            setBrush(prev => {
                if (!prev) {
                    return { left: 0, right: 0.08 };
                }
                const span = prev.right - prev.left;
                let newLeft = prev.left + PLAY_STEP;
                if (newLeft + span >= 1) {
                    setTimeout(() => {
                        stopPlay();
                        setBrush(null);
                        if (onRangeChange) {
                            onRangeChange(null);
                        }
                    }, 0);
                    return null;
                }
                return { left: newLeft, right: newLeft + span };
            });
        }, PLAY_INTERVAL_MS);
        return () => {
            clearInterval(playRef.current);
            playRef.current = null;
        };
    }, [playing, stopPlay, onRangeChange]);

    useEffect(() => {
        if (dragging.current && playing) {
            stopPlay();
        }
    });

    const togglePlay = useCallback(() => {
        if (playing) {
            stopPlay();
        } else {
            startPlay();
        }
    }, [playing, stopPlay, startPlay]);

    // ── Draw ────────────────────────────────────────────────
    useEffect(() => {
        const cvs = canvasRef.current;
        if (!cvs || !bins.length) {
            return;
        }
        const ctx = cvs.getContext('2d');
        const dpr = window.devicePixelRatio || 1;

        const w = dims.w;
        const h = dims.h;

        // Backing store = CSS pixels × devicePixelRatio
        cvs.width  = w * dpr;
        cvs.height = h * dpr;
        // Lock CSS size to exact measured pixels — prevents stretching
        cvs.style.width  = w + 'px';
        cvs.style.height = h + 'px';
        ctx.scale(dpr, dpr);
        ctx.clearRect(0, 0, w, h);

        const barAreaTop = AXIS_H + BAR_PAD_TOP;
        const barAreaH   = h - barAreaTop - BAR_PAD_BOT;
        const barW = w / bins.length;

        // ── Top axis: tick lines + labels ────────────────────
        ctx.save();
        ctx.font = '10px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
        ctx.textBaseline = 'top';

        // Minor ticks (short marks, no labels, no guide lines)
        ctx.strokeStyle = 'rgba(142, 245, 142, 0.12)';
        ctx.lineWidth = 1;
        for (const tick of ticksData.minorTicks) {
            const ratio = (tick.ts - minT) / range;
            const x = Math.round(ratio * w) + 0.5;
            ctx.beginPath();
            ctx.moveTo(x, AXIS_H);
            ctx.lineTo(x, AXIS_H - 3);
            ctx.stroke();
        }

        // Major ticks (guide lines + tick marks + labels)
        let lastLabelRight = -Infinity;
        for (const tick of ticksData.majorTicks) {
            const ratio = (tick.ts - minT) / range;
            const x = Math.round(ratio * w) + 0.5;

            // Vertical guide line through bars
            ctx.strokeStyle = 'rgba(142, 245, 142, 0.07)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(x, barAreaTop);
            ctx.lineTo(x, h);
            ctx.stroke();

            // Tick mark
            ctx.strokeStyle = 'rgba(142, 245, 142, 0.25)';
            ctx.beginPath();
            ctx.moveTo(x, AXIS_H);
            ctx.lineTo(x, AXIS_H - 5);
            ctx.stroke();

            const labelW = ctx.measureText(tick.label).width;
            let labelX = x - labelW / 2;
            labelX = Math.max(2, Math.min(labelX, w - labelW - 2));

            if (labelX > lastLabelRight + 8) {
                ctx.fillStyle = 'rgba(142, 245, 142, 0.55)';
                ctx.textAlign = 'left';
                ctx.fillText(tick.label, labelX, 3);
                lastLabelRight = labelX + labelW;
            }
        }

        // Axis baseline
        ctx.strokeStyle = 'rgba(142, 245, 142, 0.12)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, AXIS_H + 0.5);
        ctx.lineTo(w, AXIS_H + 0.5);
        ctx.stroke();
        ctx.restore();

        // ── Bars ────────────────────────────────────────────
        for (let i = 0; i < bins.length; i++) {
            const rawH = (bins[i] / maxBin) * barAreaH;
            const barH = Math.max(1, Math.round(rawH));
            const x    = Math.round(i * barW);
            const xEnd = Math.round((i + 1) * barW) - 1;
            const bw   = Math.max(1, xEnd - x);
            const y    = Math.round(barAreaTop + barAreaH - barH);

            let inBrush = true;
            if (brush) {
                const binL = i / bins.length;
                const binR = (i + 1) / bins.length;
                inBrush = binR > brush.left && binL < brush.right;
            }

            ctx.fillStyle = inBrush
                ? 'rgba(142, 245, 142, 0.75)'
                : 'rgba(142, 245, 142, 0.12)';

            const r = Math.min(1.5, bw / 2);
            ctx.beginPath();
            ctx.roundRect(x, y, bw, barH, [r, r, 0, 0]);
            ctx.fill();
        }

        // ── Brush overlay ───────────────────────────────────
        if (brush) {
            const bx  = Math.round(brush.left * w);
            const bx2 = Math.round(brush.right * w);
            const bw  = bx2 - bx;

            // Dim outside
            ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
            ctx.fillRect(0, barAreaTop, bx, barAreaH);
            ctx.fillRect(bx2, barAreaTop, w - bx2, barAreaH);

            // Selection rect
            ctx.fillStyle = 'rgba(255, 255, 255, 0.06)';
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.roundRect(bx + 0.5, barAreaTop + 0.5, bw - 1, barAreaH - 1, 3);
            ctx.fill();
            ctx.stroke();

            // Handles
            const hw = 6;
            ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
            ctx.beginPath();
            ctx.roundRect(bx - hw / 2, barAreaTop, hw, barAreaH, 2);
            ctx.fill();
            ctx.beginPath();
            ctx.roundRect(bx2 - hw / 2, barAreaTop, hw, barAreaH, 2);
            ctx.fill();

            // Grip lines
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
            ctx.lineWidth = 1;
            const midY = barAreaTop + barAreaH / 2;
            for (const hx of [bx, bx2]) {
                ctx.beginPath();
                ctx.moveTo(hx - 1, midY - 6);
                ctx.lineTo(hx - 1, midY + 6);
                ctx.stroke();
                ctx.beginPath();
                ctx.moveTo(hx + 1, midY - 6);
                ctx.lineTo(hx + 1, midY + 6);
                ctx.stroke();
            }

            // ── Brush date labels ───────────────────────────
            const startMs = minT + brush.left * range;
            const endMs   = minT + brush.right * range;
            ctx.save();
            ctx.font = '9px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
            ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
            ctx.textBaseline = 'bottom';

            const leftLabel = formatAxisLabel(startMs, true);
            ctx.textAlign = 'right';
            ctx.fillText(leftLabel, bx - 4, barAreaTop - 1);

            const rightLabel = formatAxisLabel(endMs, true);
            ctx.textAlign = 'left';
            ctx.fillText(rightLabel, bx2 + 4, barAreaTop - 1);
            ctx.restore();
        }
    }, [bins, maxBin, dims, brush, ticksData, minT, range]);

    // ── Pointer helpers ─────────────────────────────────────
    const localX = useCallback((e) => {
        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect || rect.width === 0) {
            return 0;
        }
        return Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    }, []);

    const onPointerDown = useCallback(
        (e) => {
            if (playing) {
                stopPlay();
            }
            const ratio = localX(e);
            const rect = canvasRef.current?.getBoundingClientRect();
            const cw = rect ? rect.width : dims.w;

            if (brush) {
                const lPx = brush.left * cw;
                const rPx = brush.right * cw;
                const px  = ratio * cw;

                if (Math.abs(px - lPx) < HANDLE_W * 2) {
                    dragging.current = 'left';
                    dragStart.current = { x: e.clientX, left: brush.left, right: brush.right, cw };
                    e.currentTarget.setPointerCapture(e.pointerId);
                    return;
                }
                if (Math.abs(px - rPx) < HANDLE_W * 2) {
                    dragging.current = 'right';
                    dragStart.current = { x: e.clientX, left: brush.left, right: brush.right, cw };
                    e.currentTarget.setPointerCapture(e.pointerId);
                    return;
                }
                if (ratio > brush.left && ratio < brush.right) {
                    dragging.current = 'body';
                    dragStart.current = { x: e.clientX, left: brush.left, right: brush.right, cw };
                    e.currentTarget.setPointerCapture(e.pointerId);
                    return;
                }
            }

            setBrush({ left: ratio, right: ratio });
            dragging.current = 'new';
            dragStart.current = { x: e.clientX, anchor: ratio, left: ratio, right: ratio, cw: (canvasRef.current?.getBoundingClientRect()?.width || dims.w) };
            e.currentTarget.setPointerCapture(e.pointerId);
        },
        [brush, localX, dims.w, playing, stopPlay],
    );

    const onPointerMove = useCallback(
        (e) => {
            if (!dragging.current) {
                return;
            }
            const dx = e.clientX - dragStart.current.x;
            const cw = dragStart.current.cw || dims.w;
            const dRatio = dx / cw;

            let newBrush;
            if (dragging.current === 'left') {
                const nl = Math.max(0, Math.min(dragStart.current.right - 0.005, dragStart.current.left + dRatio));
                newBrush = { left: nl, right: dragStart.current.right };
            } else if (dragging.current === 'new') {
                const anchor = dragStart.current.anchor;
                const cur = Math.max(0, Math.min(1, anchor + dRatio));
                newBrush = cur >= anchor
                    ? { left: anchor, right: cur }
                    : { left: cur, right: anchor };
            } else if (dragging.current === 'right') {
                const nr = Math.min(1, Math.max(dragStart.current.left + 0.005, dragStart.current.right + dRatio));
                newBrush = { left: dragStart.current.left, right: nr };
            } else {
                const span = dragStart.current.right - dragStart.current.left;
                let nl = dragStart.current.left + dRatio;
                if (nl < 0) {
                    nl = 0;
                }
                if (nl + span > 1) {
                    nl = 1 - span;
                }
                newBrush = { left: nl, right: nl + span };
            }
            setBrush(newBrush);
        },
        [dims.w],
    );

    const onPointerUp = useCallback(
        (e) => {
            if (!dragging.current) {
                return;
            }
            dragging.current = null;
            e.currentTarget.releasePointerCapture(e.pointerId);

            if (brush && brush.right - brush.left < 0.005) {
                setBrush(null);
                if (onRangeChange) {
                    onRangeChange(null);
                }
                return;
            }
            if (brush && onRangeChange) {
                onRangeChange([minT + brush.left * range, minT + brush.right * range]);
            }
        },
        [brush, minT, range, onRangeChange],
    );

    // Notify parent during drag/playback
    useEffect(() => {
        if (!brush) {
            return;
        }
        if (onRangeChange) {
            onRangeChange([minT + brush.left * range, minT + brush.right * range]);
        }
    }, [brush, minT, range, onRangeChange]);

    // Double-click to clear
    const onDoubleClick = useCallback(() => {
        stopPlay();
        setBrush(null);
        if (onRangeChange) {
            onRangeChange(null);
        }
    }, [onRangeChange, stopPlay]);

    if (!bins.length) {
        return null;
    }

    return (
        <div className="timeline-wrap" ref={wrapRef}>
            <button
                className="timeline-play-btn"
                onClick={togglePlay}
                title={playing ? 'Pause' : 'Play'}
                aria-label={playing ? 'Pause timeline' : 'Play timeline'}
            >
                {playing ? '⏸' : '▶'}
            </button>
            <canvas
                ref={canvasRef}
                className="timeline-canvas"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onDoubleClick={onDoubleClick}
            />
        </div>
    );
}